import { Component } from '@angular/core';
import { Location } from '@angular/common';
import { v4 as uuidv4 } from 'uuid';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environment';
import { emsCompetition } from 'src/models/emsModels';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationBar } from 'src/services/notification.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogDialog } from '../confirm-dialog/confirm-dialog.component';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-competition-edit',
  templateUrl: './competition-edit.component.html',
  styleUrl: './competition-edit.component.scss'
})
export class CompetitionEditComponent {

  notificationBar: NotificationBar;
  competitionGroup = new FormGroup({
    nameControl: new FormControl('', [Validators.required]),
    stageControl: new FormControl('', [Validators.required]),
    logoControl: new FormControl('', []),
    backgroundControl: new FormControl('', []),
  });
  isProcessingCompDelete = false;
  isProcessingLogoDelete = false;
  isProcessingBackgroundDelete = false;
  workspaceID = "";
  competitionID = "";
  logoUploadProgress = 0;
  backgroundUploadProgress = 0;
  placeholderImage = environment.placeholderImage;
  isActive = true;
  activeCheckboxControl = new FormControl(true);
  competitionDBRef: any;

  constructor(private title: Title, private router: Router, private route: ActivatedRoute, private location: Location, private snackBar: MatSnackBar, public dialog: MatDialog, private fireStorage: AngularFireStorage, private db: AngularFireDatabase) {
    this.notificationBar = new NotificationBar(snackBar);
   }

  ngOnInit() {  
    this.route.paramMap.subscribe(params => {
      this.workspaceID  = this.location.path().split("/")[1];
      this.competitionID  = this.location.path().split("/")[3];
      this.loadCompetition();
    });
  }

  ngOnDestroy() {
    if (this.competitionDBRef) {
      this.competitionDBRef.off('value');
    };
  }

  loadCompetition() {
    this.competitionDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions/' + this.competitionID);
    this.competitionDBRef.on('value', x => {
      var competition = x.toJSON() as emsCompetition;
      this.title.setTitle(competition.name + " - Edit Competition - EsportsMS");
      this.competitionGroup.controls.nameControl.setValue(competition.name);
      this.competitionGroup.controls.stageControl.setValue(competition.stage);
      this.competitionGroup.controls.logoControl.setValue(competition.logo);
      this.competitionGroup.controls.backgroundControl.setValue(competition.background);
      this.activeCheckboxControl.setValue(competition.active)
    });
  }

  changeName() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions/' + this.competitionID).update({
      name: this.competitionGroup.controls.nameControl.value
    });
  }

  changeStage() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions/' + this.competitionID).update({
      stage: this.competitionGroup.controls.stageControl.value
    });
  }

  async uploadLogo(event: any) {
    if (this.competitionGroup.controls.logoControl.value && this.competitionGroup.controls.logoControl.value.length > 0) {
      await this.deleteLogo(true);
    }
    var fileType = event.target.files[0].name.split('.').pop();
    fileType = "." + fileType;
    const fileRef = this.fireStorage.ref("/resources/esportsms/w/" + this.workspaceID + "/c/" + `${uuidv4()}` + fileType);
    const task = fileRef.put(event.target.files[0]);
    task.percentageChanges().subscribe((progress) => {
      this.logoUploadProgress = progress
    });
    task.snapshotChanges().subscribe({
      complete: () => {
        fileRef.getMetadata().subscribe( x => {
          this.competitionGroup.controls.logoControl.setValue(environment.assetDomain + x.fullPath);
          this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions/' + this.competitionID).update({
            logo: environment.assetDomain + x.fullPath
          });
          this.logoUploadProgress = 0;
          this.notificationBar.show("Logo upload!")
        })
      },
      error: (error) => {
        this.notificationBar.show("Upload failed!")
        this.logoUploadProgress = 0;
      }
    });
  }

  async deleteLogo(skipConfirm: boolean) {
    this.isProcessingLogoDelete = true;
    var res = false;
    if (skipConfirm == false) {
      var dialogRef = this.dialog.open(ConfirmDialogDialog, {
        data: {
          actionType: "delete",
          message: "Are you sure you want to delete the logo?",
        }
      });
      res = await dialogRef.afterClosed().toPromise();
    }
    else {
      res = true;
    }
    if (res == true) {
      var picturePath = new URL(this.competitionGroup.controls.logoControl.value).pathname;
      var fileRef = this.fireStorage.ref(picturePath);
      fileRef.delete();
      this.competitionGroup.controls.logoControl.setValue("");
      this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions/' + this.competitionID).update({
        logo: ""
      });
      if (skipConfirm == false) {
        this.notificationBar.show("Logo deleted!");
      }
    }
    this.isProcessingLogoDelete = false;
  }

  async uploadBackground(event: any) {
    if (this.competitionGroup.controls.backgroundControl.value && this.competitionGroup.controls.backgroundControl.value.length > 0) {
      await this.deleteBackground(true);
    }
    var fileType = event.target.files[0].name.split('.').pop();
    fileType = "." + fileType;
    const fileRef = this.fireStorage.ref("/resources/esportsms/w/" + this.workspaceID + "/c/" + `${uuidv4()}` + fileType);
    const task = fileRef.put(event.target.files[0]);
    task.percentageChanges().subscribe((progress) => {
      this.backgroundUploadProgress = progress
    });
    task.snapshotChanges().subscribe({
      complete: () => {
        fileRef.getMetadata().subscribe( x => {
          this.competitionGroup.controls.backgroundControl.setValue(environment.assetDomain + x.fullPath);
          this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions/' + this.competitionID).update({
            background: environment.assetDomain + x.fullPath
          });
          this.backgroundUploadProgress = 0;
          this.notificationBar.show("Background upload!")
        })
      },
      error: (error) => {
        this.notificationBar.show("Upload failed!")
        this.backgroundUploadProgress = 0;
      }
    });
  }

  async deleteBackground(skipConfirm: boolean) {
    this.isProcessingBackgroundDelete = true;
    var res = false;
    if (skipConfirm == false) {
      var dialogRef = this.dialog.open(ConfirmDialogDialog, {
        data: {
          actionType: "delete",
          message: "Are you sure you want to delete the background?",
        }
      });
      res = await dialogRef.afterClosed().toPromise();
    }
    else {
      res = true;
    }    
    if (res == true) {
      var picturePath = new URL(this.competitionGroup.controls.backgroundControl.value).pathname;
      var fileRef = this.fireStorage.ref(picturePath);
      fileRef.delete();
      this.competitionGroup.controls.backgroundControl.setValue("");
      this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions/' + this.competitionID).update({
        background: ""
      });
      if (skipConfirm == false) {
        this.notificationBar.show("Background deleted!");
      }
    }
    this.isProcessingBackgroundDelete = false;
  }

  changeActivation(event: MatCheckboxChange) {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions/' + this.competitionID).update({
      active: event.checked
    });
  }

  async deleteCompetition() {
    this.isProcessingCompDelete = true;
    var dialogRef = this.dialog.open(ConfirmDialogDialog, {
      data: {
        actionType: "delete",
        message: "Are you sure you want to delete " + this.competitionGroup.controls.nameControl.value + "?",
      }
    });
    return dialogRef.afterClosed().toPromise().then(async res => {
      if (res == true) {
        try {
          var picturePath = new URL(this.competitionGroup.controls.logoControl.value).pathname;
          var fileRef = this.fireStorage.ref(picturePath);
          fileRef.delete();
        }
        catch { }
        try {
          picturePath = new URL(this.competitionGroup.controls.backgroundControl.value).pathname;
          fileRef = this.fireStorage.ref(picturePath);
          fileRef.delete();
        } catch { }
        this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions/' + this.competitionID).remove().then( x => {
          this.router.navigate([ this.workspaceID + '/dashboard']);
          this.notificationBar.show("Competition deleted!");
        }, err => {
            this.notificationBar.showUnknownError();
        })
      }
      this.isProcessingCompDelete = false;
    });
  }
}